import { Attendee } from "../types/event";

/**
 * Who is running an event — the staff member event-manager-v2 labels
 * "Event Owner".
 *
 * TWO SOURCES, one shape. On a single-event read the people are inside
 * `Attendees`: `event/admin/{id}?attendees=1` resolves every ContactPersonal
 * linked to the event — staff as well as students, there is no student-only
 * filter — and stamps each row with the `Type` off its EventRelation edge. So a
 * supervisor is an attendee whose `Type` says so. On the list, `supervisors=1`
 * returns the same people already filtered, as `Supervisors`.
 *
 * The rows are deliberately identical in their four name fields and carry
 * `Type`, so everything below reads either source without knowing which it has.
 *
 * This key was a trap until papi grew the list parameter, and the history is
 * worth keeping: S1 built `Supervisors` at read time in the legacy Azure
 * Function (`datatown-api-functions/EventManagement`) and it was never stored
 * on the document, so after the S1 cutover anything reading it rendered blank —
 * which is what event-manager-v2's details tab still does, since only the LIST
 * endpoint sends the key.
 *
 * ⚠️ PII. These are staff names. Display only; never log them.
 */

/**
 * The supervisors on a roll, in the order the API returned them.
 *
 * `Type` is absent on events synced before the relation carried one (see
 * `attendee_relation_types` in pymble-apis, which reports it rather than
 * filtering on it). Those events yield an empty list rather than a wrong
 * answer — the data is genuinely not there, and nothing here can invent it.
 *
 * Takes the roll rather than the event so a caller can memoise on
 * `event.Attendees`, which changes when the fetch lands, instead of on the
 * draft, which changes on every keystroke.
 */
export function supervisors(attendees: Attendee[] | undefined): Attendee[] {
	if (!Array.isArray(attendees)) return [];
	return attendees.filter((a) => a.Type === "Supervisor");
}

/**
 * One attendee's name in reading order — "Jane Smith", not "Smith, Jane".
 *
 * The discrete fields first, as on the attendee roll: `Name` is the API's
 * combined `"FamilyName, PreferredName"` and splitting it on the comma breaks
 * on any name that legitimately contains one, so it is only the fallback for a
 * deployment predating the separate fields.
 *
 * `PreferredName` ahead of `GivenName` for the same reason the roll prefers it:
 * it is what the person is actually called. The API already falls it back to
 * the legal given name before sending.
 */
export function attendeeName(attendee: Attendee | undefined): string {
	if (!attendee) return "";

	const given = (attendee.PreferredName || attendee.GivenName || "").trim();
	const family = (attendee.FamilyName ?? "").trim();
	if (given || family) return [given, family].filter(Boolean).join(" ");

	const [splitFamily = "", splitGiven = ""] = (attendee.Name ?? "")
		.split(",")
		.map((part) => part.trim());
	return [splitGiven, splitFamily].filter(Boolean).join(" ");
}

/** Every supervisor's name, ready to display. Empty when none are recorded. */
export function supervisorNames(attendees: Attendee[] | undefined): string[] {
	return supervisors(attendees)
		.map(attendeeName)
		.filter((name) => name.length > 0);
}
